import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import classNames from "classnames";

import {FormGroup, FormGroupProps, PopoverPosition} from "@blueprintjs/core";
// 关键：导入 PopoverPosition 枚举
import { DateInput2, DateInput2Props } from "@blueprintjs/datetime2";
import { localUtils } from "@/shared/utils/dateUtils";
import "date-fns/locale/zh-CN";
import "dayjs/locale/zh-cn";

export interface DatePickerProps
    extends Omit<FormGroupProps, "children">,
        Omit<DateInput2Props, "formatDate" | "parseDate"> {
    small?: boolean;
    large?: boolean;
    formatType?: string;
    formatDate?: DateInput2Props["formatDate"];
    parseDate?: DateInput2Props["parseDate"];
}

export const DatePicker = ({
                               className,
                               contentClassName,
                               helperText,
                               inline,
                               intent,
                               label,
                               labelFor,
                               labelInfo,
                               style,
                               subLabel,
                               formatType,
                               placeholder = "请选择日期",
                               formatDate: customFormatDate,
                               parseDate: customParseDate,
                               inputProps,
                               small = true,
                               large,
                               ...dateRangeInput2Props
                           }: DatePickerProps) => {
    // 缓存formatDate函数
    const memoizedFormatDate = useCallback<DateInput2Props["formatDate"]>(
        (date) => {
            if (customFormatDate) return customFormatDate(date);
            return dayjs(date).format(formatType ?? "YYYY-MM-DD");
        },
        [customFormatDate, formatType]
    );

    // 缓存parseDate函数
    const memoizedParseDate = useCallback<DateInput2Props["parseDate"]>(
        (string) => {
            if (customParseDate) return customParseDate(string);
            return dayjs(string).toDate();
        },
        [customParseDate]
    );

    // 缓存DateInput2属性（修复position类型）
    const dateInputProps = useMemo(() => ({
        popoverProps:{ minimal: true, position: PopoverPosition.LEFT},
        dayPickerProps: {
            locale: "zh-cn",
            localeUtils: localUtils,
        },
        formatDate: memoizedFormatDate,
        parseDate: memoizedParseDate,
        placeholder,
        minDate: dayjs("1900-01-01").toDate(),
        maxDate: dayjs("2099-12-31").toDate(),
        outOfRangeMessage: "时间超出可选择范围",
        invalidDateMessage: "无效的时间日期",
        inputProps: {
            small,
            large,
            ...inputProps,
        },
        ...dateRangeInput2Props,
    }), [
        memoizedFormatDate,
        memoizedParseDate,
        placeholder,
        small,
        large,
        inputProps,
        dateRangeInput2Props,
    ]);

    // 缓存FormGroup属性
    const formGroupProps = useMemo(() => ({
        className: classNames("!mb-0", className),
        contentClassName,
        helperText,
        inline,
        intent,
        label,
        labelFor,
        labelInfo,
        style,
        subLabel,
    }), [
        className,
        contentClassName,
        helperText,
        inline,
        intent,
        label,
        labelFor,
        labelInfo,
        style,
        subLabel,
    ]);

    return (
        <FormGroup {...formGroupProps}>
            <DateInput2 {...dateInputProps} />
        </FormGroup>
    );
};
